import { useState, useEffect } from 'preact/hooks';
import { atlasHomeUrl, resolveAtlasLang } from '@fcbh/atlas-schema';
import { effect } from '@preact/signals';
import { colorProfile } from '../state/color-profile.js';
import { route } from '../state/store.js';
import { vaultState, isPinConfigured } from '../state/security/vault.js';
import { VaultGate } from './security/VaultGate.js';
import { PlayerBar } from './PlayerBar.js';
import { Chapter } from './Chapter.js';
import { ChatBubble } from './Ask.js';
import { ChapterPicker } from './ChapterPicker.js';
import { PlansModal } from './PlansModal.js';
import { appendNextChapter, initPlayerController } from '../audio/controller.js';
import { appendedChapters, books, currentChapter, chapterLoading } from '../state/chapter-store.js';
import { useKeyboardShortcuts } from './use-keyboard-shortcuts.js';
import { currentStreak, addFreezeToken } from '../state/progress-store.js';
import { InstallPrompt, markEngaged } from './InstallPrompt.js';
import { OnboardingCoach } from './OnboardingCoach.js';
import { playback } from '../state/store.js';
import { SearchPanel } from './SearchPanel.js';
import { CompareChapter } from './CompareChapter.js';
import { lastVisited } from '../state/history-store.js';
import { getReadingPosition, setPendingScroll } from '../state/reading-position.js';
import '../state/color-profile.js'; // side-effect: boots profile persistence
import { parseOsis } from '@fcbh/atlas-schema';
import { publisher } from '../publisher/mode.js';
import { bootBadgeEvaluator } from '../state/badge-evaluator.js';
import { BADGE_CATALOG } from '../state/badges-store.js';
import { MilestoneToast } from './MilestoneToast.js';
import { ShareCardSheet, shareCardRequest } from './ShareCardSheet.js';
import type { ShareCardMode } from './ShareCardSheet.js';
import { PlaylistPanel } from './PlaylistPanel.js';

/**
 * Resolve the Atlas locale at call-time so it reflects the publisher manifest
 * once it's loaded. Publisher bcp47 (e.g. "vi") is preferred; languageIso
 * (ISO 639-3, e.g. "vie") is the fallback so un-prefixed deploys still work.
 * Falls back through navigator.language then resolveAtlasLang's built-in
 * default ("en") when none of the above match the 53 Atlas locales.
 */
function getAtlasLang(): string {
  const pub = publisher.peek();
  return resolveAtlasLang([
    pub?.bcp47,
    pub?.languageIso,
    typeof navigator !== 'undefined' ? navigator.language : undefined,
  ]);
}

/**
 * Build the Atlas link with a fresh `returnTo` reflecting the user's
 * current chapter — so the Bible button in Atlas's header sends them
 * back here, not just to the reader root.
 */
function buildAtlasHref(book: string, chapter: number): string {
  const lang = getAtlasLang();
  const palette = colorProfile.peek();
  if (typeof window === 'undefined') {
    return atlasHomeUrl({ lang, from: 'fcbh-reader', palette });
  }
  try {
    const u = new URL(window.location.origin + window.location.pathname);
    u.searchParams.set('book', book);
    u.searchParams.set('chapter', String(chapter));
    return atlasHomeUrl({
      lang,
      from: 'fcbh-reader',
      returnTo: u.toString(),
      palette,
    });
  } catch {
    return atlasHomeUrl({ lang, from: 'fcbh-reader', palette });
  }
}

type ToastEntry = { badgeId: string; icon: string; label: string };

export function App() {
  const secureMode =
    typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>).__SECURE_MODE__ : null;
  const requiresGate =
    secureMode === 'moderate' || secureMode === 'restricted' || isPinConfigured();
  if (requiresGate && vaultState.value !== 'unlocked') {
    return <VaultGate />;
  }

  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [toastQueue, setToastQueue] = useState<ToastEntry[]>([]);
  const [showingToast, setShowingToast] = useState<ToastEntry | null>(null);
  const [milestoneShareMode, setMilestoneShareMode] = useState<ShareCardMode | null>(null);

  useEffect(() => {
    initPlayerController();
  }, []);

  useEffect(() => {
    const cleanup = bootBadgeEvaluator((id) => {
      const meta = BADGE_CATALOG.find((b) => b.id === id);
      if (!meta) return;
      if (id === 'streak-7' || id === 'streak-30' || id === 'streak-100') addFreezeToken();
      setToastQueue((q) => [...q, { badgeId: id, icon: meta.icon, label: meta.label }]);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    if (!showingToast && toastQueue.length > 0) {
      setShowingToast(toastQueue[0]!);
      setToastQueue((q) => q.slice(1));
    }
  }, [toastQueue, showingToast]);

  // Watch for verse share requests from VerseMenu
  useEffect(() => {
    return effect(() => {
      const req = shareCardRequest.value;
      if (req) {
        shareCardRequest.value = null;
        setMilestoneShareMode(req);
      }
    });
  }, []);

  useKeyboardShortcuts();
  useEngagementGates();
  useSwMessageNav();
  useDeepLinkOnLoad();

  const chapter = currentChapter.value;
  const tail = appendedChapters.value;
  const loading = chapterLoading.value;
  const r = route.value;
  useContinuousScroll();

  return (
    <div class="reader-root">
      <header class="reader-top-bar">
        <button
          type="button"
          class="reader-top-bar-icon"
          aria-label="Reading plans"
          onClick={() => setPlansOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
          </svg>
        </button>

        <div class="reader-top-bar-center">
          <span class="reader-top-crumb">{chapter?.source?.translation ?? 'Bible'}</span>
          <button
            type="button"
            class="reader-top-title"
            onClick={() => setPickerOpen(true)}
            aria-haspopup="dialog"
          >
            <span>
              {chapter ? chapter.bookName : r.book}{' '}
              <span class="ch-accent">{chapter ? chapter.chapter : r.chapter}</span>
            </span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <StreakBadge />
          <button
            type="button"
            class="reader-top-bar-icon"
            aria-label="AI Playlist"
            onClick={() => setPlaylistOpen(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <circle cx="3" cy="6" r="1" style="fill:currentColor;stroke:none"/>
              <circle cx="3" cy="12" r="1" style="fill:currentColor;stroke:none"/>
              <circle cx="3" cy="18" r="1" style="fill:currentColor;stroke:none"/>
            </svg>
          </button>
          <a
            class="reader-top-bar-icon"
            href={buildAtlasHref(r.book, r.chapter)}
            target="_blank"
            rel="noreferrer"
            aria-label="Open Atlas"
            style={{ textDecoration: 'none', fontSize: '13px', fontWeight: 600, letterSpacing: '.02em', color: 'var(--muted)' }}
          >
            Atlas
          </a>
          <button
            type="button"
            class="reader-top-bar-icon"
            aria-label="Search scripture"
            onClick={() => setSearchOpen(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
        </div>
      </header>
      <div class="reader-top-divider" />
      <main class="reader-main">
        {chapter ? <Chapter chapter={chapter} /> : <ChapterSkeleton loading={loading} />}
        <CompareChapter />
        {tail.map((c) => (
          <Chapter key={`${c.bookId}.${c.chapter}`} chapter={c} />
        ))}
      </main>
      <PlayerBar />
      <InstallPrompt />
      <OnboardingCoach />
      <ChatBubble />
      {pickerOpen ? (
        <ChapterPicker
          books={books.value}
          activeBook={r.book}
          activeChapter={r.chapter}
          onPick={(book, ch) => {
            route.value = { book, chapter: ch };
          }}
          onClose={() => setPickerOpen(false)}
          onOpenPlans={() => setPlansOpen(true)}
          onOpenPlaylist={() => { setPickerOpen(false); setPlaylistOpen(true); }}
        />
      ) : null}
      {searchOpen ? <SearchPanel onClose={() => setSearchOpen(false)} /> : null}
      {plansOpen ? <PlansModal onClose={() => setPlansOpen(false)} /> : null}
      {playlistOpen ? (
        <PlaylistPanel
          onClose={() => setPlaylistOpen(false)}
          onPick={(book, ch) => {
            route.value = { book, chapter: ch };
            setPlaylistOpen(false);
          }}
        />
      ) : null}
      {showingToast ? (
        <MilestoneToast
          badgeId={showingToast.badgeId}
          icon={showingToast.icon}
          label={showingToast.label}
          onShare={() => {
            setMilestoneShareMode({ kind: 'milestone', icon: showingToast.icon, label: showingToast.label });
            setShowingToast(null);
          }}
          onDismiss={() => setShowingToast(null)}
        />
      ) : null}
      {milestoneShareMode ? (
        <ShareCardSheet
          mode={milestoneShareMode}
          onClose={() => setMilestoneShareMode(null)}
        />
      ) : null}
    </div>
  );
}

function StreakBadge() {
  const days = currentStreak.value;
  if (days < 1) return null;
  return (
    <span class="reader-streak" title={`${days}-day reading streak`}>
      <span class="reader-streak-num">{days}</span>
      <span class="reader-streak-label">day{days === 1 ? '' : 's'}</span>
    </span>
  );
}

function ChapterSkeleton({ loading }: { loading: boolean }) {
  return (
    <article class="chapter">
      <header class="chapter-header">
        <p class="chapter-eyebrow">{loading ? 'Loading' : 'No content'}</p>
        <h1 class="chapter-title">…</h1>
      </header>
    </article>
  );
}

const APPEND_TRIGGER_PX = 1200;

/**
 * Continuous-scroll wiring: append the next chapter when the user scrolls
 * within ~1200px of the page bottom. Audio + karaoke + media-session stay
 * bound to whatever chapter the user explicitly picked — auto-advance
 * (via the `ended` event on the audio element) handles in-playback
 * chapter changes. Pure-read scrolling just extends the page.
 */
function useSwMessageNav(): void {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    const onMsg = (e: MessageEvent): void => {
      const data = e.data as { type?: string; book?: string; chapter?: number } | undefined;
      if (data?.type === 'navigate' && data.book && Number.isFinite(data.chapter)) {
        route.value = { book: data.book, chapter: data.chapter as number };
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, []);
}

// USFM book code per scripture-burrito; the OSIS values returned by
// parseOsis (Mat, Gen, etc.) match the bible-store keys after uppercasing.
function osisToBookChapter(raw: string): { book: string; chapter: number } | null {
  const parts = parseOsis(raw);
  if (!parts) return null;
  const chapter = parts.chapter ?? 1;
  if (!Number.isFinite(chapter) || chapter <= 0) return null;
  return { book: parts.book.toUpperCase(), chapter };
}

function useDeepLinkOnLoad(): void {
  // Honor `?book=…&chapter=…` and `?ref=Mat.21.10` once on load — used by
  // notification clicks, Atlas's "Return to reader" button, and Atlas's
  // in-article scripture chips (which send OSIS). Falls back to the
  // user's last visited chapter so re-opening the reader doesn't dump
  // them at Matthew 1.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const book = params.get('book');
      const chapter = Number(params.get('chapter'));
      if (book && Number.isFinite(chapter) && chapter > 0) {
        route.value = { book, chapter };
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      const ref = params.get('ref');
      if (ref) {
        const parsed = osisToBookChapter(ref);
        if (parsed) {
          route.value = parsed;
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
      }
      const last = lastVisited();
      if (last) {
        route.value = { book: last.bookId, chapter: last.chapter };
        // If we have a verse-level reading position for this chapter, queue
        // a scroll to it. ChapterWithVerses will consume this on mount.
        const pos = getReadingPosition();
        if (
          pos &&
          pos.bookId === last.bookId.toUpperCase() &&
          pos.chapter === last.chapter
        ) {
          setPendingScroll(pos.verseRef);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);
}

function useEngagementGates(): void {
  // Flip the install-prompt engagement signal after any meaningful
  // interaction so the banner doesn't surface on first load.
  useEffect(() => {
    const scrollY0 = window.scrollY;
    const onScroll = (): void => {
      if (Math.abs(window.scrollY - scrollY0) > 400) {
        markEngaged();
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const stop = effect(() => {
      if (playback.value.currentSec >= 30) {
        markEngaged();
        stop();
      }
    });
    return () => {
      window.removeEventListener('scroll', onScroll);
      stop();
    };
  }, []);
}

function useContinuousScroll(): void {
  useEffect(() => {
    let pending = false;

    function evaluate(): void {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        const scrolled = window.scrollY + window.innerHeight;
        const bottom = document.documentElement.scrollHeight;
        if (bottom - scrolled < APPEND_TRIGGER_PX) {
          void appendNextChapter();
        }
      });
    }

    window.addEventListener('scroll', evaluate, { passive: true });
    evaluate();
    return () => window.removeEventListener('scroll', evaluate);
  }, []);
}
